import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "details-stack" }
const _hoisted_2 = {
  key: 0,
  class: "plan-card"
}
const _hoisted_3 = { class: "plan-billing-toggle" }
const _hoisted_4 = { class: "billing-toggle-container" }
const _hoisted_5 = { class: "custom-toggle" }
const _hoisted_6 = { class: "toggle-track" }
const _hoisted_7 = { class: "plan-header" }
const _hoisted_8 = { class: "plan-title" }
const _hoisted_9 = { class: "plan-name" }
const _hoisted_10 = { class: "plan-pricing" }
const _hoisted_11 = { class: "price-with-period" }
const _hoisted_12 = {
  key: 0,
  class: "plan-price"
}
const _hoisted_13 = { class: "billing-period" }
const _hoisted_14 = {
  key: 0,
  class: "monthly-equivalent"
}
const _hoisted_15 = {
  key: 0,
  class: "savings-text"
}
const _hoisted_16 = { class: "plan-highlight" }
const _hoisted_17 = { class: "plan-features" }

import { computed, onMounted, ref, watch } from "vue";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import type { MembershipPackage } from "@/shared/services/vendor-requests.api";
import { MEMBERSHIP_PACKAGES_PRESETS } from "@/modules/vendor/membership-packages.presets";
import { formatCurrency } from "@/shared/utils/utils";
import {getCompanyPlan } from "@/shared/services/vendor-requests.api";
import { useRoute } from "vue-router";


export default /*@__PURE__*/_defineComponent({
  __name: 'MembershipPlans',
  props: {
    company_id: { default: null },
    package_id: { default: "" },
    billing_interval: { default: "monthly" },
    errors: { default: () => ({}) }
  },
  emits: ["update:package_id", "update:billing_interval", "packages-loaded"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const packages = ref<MembershipPackage[]>([]);
const isLoadingPackages = ref(false);
const billingInterval = ref<"monthly" | "yearly">(props.billing_interval);

onMounted(() => {
  loadPackages();
});

watch(
  () => props.company_id,
  () => {
    loadPackages();
  },
);

const normalizePackages = (payload: any): MembershipPackage[] => {
  const raw =
    Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.packages) ? payload.packages : [];

  if (!Array.isArray(raw)) return [];

  return raw
    .filter(Boolean)
    .map((p: any) => ({
      ...p,
      id: String(p?.id ?? ""),
    }))
    .filter((p: any) => p.id);
};
const route = useRoute();
function normalizeCompanyId(value: unknown): number | null {
  let raw: any = value;
  if (Array.isArray(raw)) raw = raw[0] ?? null;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && /^\d+$/.test(raw)) return Number(raw);
  // Attempt numeric coercion as a last resort
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function resolveCompanyId(): number | null {
  return normalizeCompanyId(props.company_id) ?? normalizeCompanyId(route.params.companyId);
}


const loadPackages = async () => {
  isLoadingPackages.value = true;
  try {

    let data;
    // if (isVendorMode()) {
    //   const rawCompanyId = router.currentRoute.value.query.company_id;
    //   let companyId: string | number = "";

    //   if (Array.isArray(rawCompanyId)) {
    //     companyId = rawCompanyId[0] ?? "";
    //   } else {
    //     companyId = rawCompanyId ?? "";
    //   }

    //   // convert numeric string to number so the argument matches string | number
    //   if (typeof companyId === "string" && /^\d+$/.test(companyId)) {
    //     companyId = Number(companyId);
    //   }

      data = await getCompanyPlan(resolveCompanyId() ?? undefined);
    // }else{
    //   data = await getMembershipPackageList();
    // }
    const list = normalizePackages(data);
    const hasRequiredPrices = (p: any) =>
      p &&
      (typeof p.price_monthly === "string" || typeof p.price_monthly === "number") &&
      (typeof p.price_yearly === "string" || typeof p.price_yearly === "number");
    const next = list.length && list.every(hasRequiredPrices) ? list : MEMBERSHIP_PACKAGES_PRESETS;
    packages.value = next;
    emit("packages-loaded", next);
  } catch (err) {
    packages.value = MEMBERSHIP_PACKAGES_PRESETS;
    emit("packages-loaded", MEMBERSHIP_PACKAGES_PRESETS);
    // eslint-disable-next-line no-console
    console.error("Failed to load membership packages", err);
  } finally {
    isLoadingPackages.value = false;
  }
};

const handleIntervalChange = (value: string) => {
  const next = value === "yearly" ? "yearly" : "monthly";
  billingInterval.value = next;
  emit("update:billing_interval", next);
};

const selectedPackage = computed(() => {
  const id = String(props.package_id ?? "").trim();
  if (!id) return null;
  return packages.value.find((p) => String(p.id) === id) ?? null;
});

const parsePriceNumber = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const parsed = parseFloat(value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const selectedPackagePriceRaw = computed(() => {
  const pkg = selectedPackage.value;
  if (!pkg) return undefined;

  if (billingInterval.value === "yearly") {
    return pkg.price_yearly;
  }

  return pkg.price_monthly;
});

const selectedPackagePriceText = computed(() => {
  const raw = selectedPackagePriceRaw.value;
  if (raw === undefined || raw === null) return "";

  const numeric = parsePriceNumber(raw);
  if (numeric === null) return String(raw);
  return formatCurrency(numeric);
});

const yearlySavings = computed(() => {
  const pkg = selectedPackage.value;
  if (!pkg || !pkg.price_monthly || !pkg.price_yearly) {
    return null;
  }

  const monthlyPrice = parsePriceNumber(pkg.price_monthly);
  const yearlyPrice = parsePriceNumber(pkg.price_yearly);
  if (monthlyPrice === null || yearlyPrice === null) return null;

  const yearlyMonthlyTotal = monthlyPrice * 12;
  const savings = yearlyMonthlyTotal - yearlyPrice;
  const savingsPercentage = Math.round((savings / yearlyMonthlyTotal) * 100);
  
  return savingsPercentage > 0 ? `${savingsPercentage}%` : null;
});

const monthlyEquivalent = computed(() => {
  const pkg = selectedPackage.value;
  if (!pkg || !pkg.price_yearly) {
    return null;
  }

  const yearlyPrice = parsePriceNumber(pkg.price_yearly);
  if (yearlyPrice === null) return null;
  
  const monthlyEq = yearlyPrice / 12;
  return `≈ ${formatCurrency(monthlyEq)}/month`;
});


return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(AppSelectField, {
                "model-value": __props.package_id,
                items: packages.value,
                "item-title": "label",
                "item-value": "id",
                label: "Membership Package",
                "label-icon": "mdi-crown-outline",
                placeholder: "Select package",
                loading: isLoadingPackages.value,
                disabled: isLoadingPackages.value,
                "error-messages": __props.errors.package_id ? [__props.errors.package_id] : [],
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (emit('update:package_id', String($event || ''))))
              }, null, 8, ["model-value", "items", "loading", "disabled", "error-messages"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (selectedPackage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "billing-header" }, null, -1)),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["toggle-option", { active: billingInterval.value === 'monthly' }]),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (handleIntervalChange('monthly')))
                    }, [...(_cache[3] || (_cache[3] = [
                      _createElementVNode("span", { class: "toggle-text" }, "Monthly", -1)
                    ]))], 2),
                    _createElementVNode("div", {
                      class: _normalizeClass(["toggle-slider", { yearly: billingInterval.value === 'yearly' }])
                    }, null, 2),
                    _createElementVNode("div", {
                      class: _normalizeClass(["toggle-option", { active: billingInterval.value === 'yearly' }]),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (handleIntervalChange('yearly')))
                    }, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("span", { class: "toggle-text" }, "Yearly", -1)
                    ]))], 2)
                  ])
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("span", _hoisted_9, _toDisplayString(selectedPackage.value.label), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                (selectedPackagePriceText.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(selectedPackagePriceText.value), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("span", _hoisted_13, "/" + _toDisplayString(billingInterval.value), 1)
              ]),
              (billingInterval.value === 'yearly')
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createElementVNode("span", null, _toDisplayString(monthlyEquivalent.value), 1),
                    (yearlySavings.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                          _createVNode(_component_v_icon, {
                            size: "10",
                            color: "success"
                          }, {
                            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                              _createTextVNode("mdi-tag", -1)
                            ]))]),
                            _: 1
                          }),
                          _createTextVNode(" Save " + _toDisplayString(yearlySavings.value), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_16, _toDisplayString(selectedPackage.value.highlight), 1),
            _createElementVNode("div", _hoisted_17, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedPackage.value.features, (feature, idx) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: idx,
                  class: "feature-row"
                }, [
                  _createVNode(_component_v_icon, {
                    size: "18",
                    color: "success"
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("mdi-check", -1)
                    ]))]),
                    _: 1
                  }),
                  _createElementVNode("span", null, _toDisplayString(feature), 1)
                ]))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})